import { Link } from '@tanstack/react-router'
import { Lock } from 'lucide-react'
import { useId, useState } from 'react'
import { buildMultiDayOccurrences, repeatOccurrences } from '~/entities/event/lib/event-occurrences'
import * as m from '~/paraglide/messages'
import type { OccurrenceForm } from '~/shared/types/event'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { DatePicker } from '~/shared/ui/date-picker'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'
import { Switch } from '~/shared/ui/switch'
import { EventOccurrencesEditor } from './event-occurrences-editor'

export interface RecurringConfig {
  enabled: boolean
  frequencyType: 'days' | 'weeks' | 'months'
  interval: number
  count: number
}

const DEFAULT_OCCURRENCE_START = '18:00'
const DEFAULT_OCCURRENCE_END = '23:00'

interface EventDateTimeSectionProps {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API is dynamically typed
  form: any
  recurringConfig?: RecurringConfig
  onRecurringConfigChange?: (config: RecurringConfig) => void
  canRecur?: boolean
}

function formatInterval(interval: number, type: 'days' | 'weeks' | 'months') {
  if (interval === 1) {
    if (type === 'days') return m.recurringDay()
    if (type === 'weeks') return m.recurringWeek()
    return m.recurringMonth()
  }

  const label =
    type === 'days'
      ? m.recurringDays()
      : type === 'weeks'
        ? m.recurringWeeks()
        : m.recurringMonths()

  return `${interval} ${label}`
}

export function EventDateTimeSection({
  form,
  recurringConfig,
  onRecurringConfigChange,
  canRecur = true,
}: EventDateTimeSectionProps) {
  const recurringToggleId = useId()
  const fromDateId = useId()
  const toDateId = useId()

  const initialOccurrences: OccurrenceForm[] = form.getFieldValue('occurrences') ?? []
  const [multiFrom, setMultiFrom] = useState(initialOccurrences[0]?.date ?? '')
  const [multiTo, setMultiTo] = useState(
    initialOccurrences[initialOccurrences.length - 1]?.date ?? '',
  )

  const currentOccurrences = (): OccurrenceForm[] => form.getFieldValue('occurrences') ?? []

  // One occurrence per day in the range, merging the user's edited per-day times.
  // Occurrences only appear once BOTH bounds are set; a range never auto-generates
  // a recurrence (that happens only via Apply).
  const buildSchedule = (from: string = multiFrom, to: string = multiTo): OccurrenceForm[] =>
    from && to
      ? buildMultiDayOccurrences(
          from,
          to,
          DEFAULT_OCCURRENCE_START,
          DEFAULT_OCCURRENCE_END,
          currentOccurrences(),
        )
      : []

  const repeatWithConfig = (block: OccurrenceForm[]): OccurrenceForm[] =>
    recurringConfig
      ? repeatOccurrences(block, {
          frequencyType: recurringConfig.frequencyType,
          interval: recurringConfig.interval,
          count: recurringConfig.count,
        })
      : block

  const setOccurrences = (next: OccurrenceForm[]) => {
    form.setFieldValue('occurrences', next)
  }

  // Changing either bound regenerates one occurrence per day at once - no manual adds.
  const handleFromChange = (value: string) => {
    setMultiFrom(value)
    setOccurrences(buildSchedule(value, multiTo))
  }

  const handleToChange = (value: string) => {
    setMultiTo(value)
    setOccurrences(buildSchedule(multiFrom, value))
  }

  const handleApplyRecurrence = () => {
    setOccurrences(repeatWithConfig(buildSchedule()))
  }

  // Enabling recurrence does NOT auto-add dates - the user applies them via
  // "Apply recurrence". Disabling reverts to the plain range schedule.
  const handleRecurringToggle = (checked: boolean) => {
    if (!recurringConfig || !onRecurringConfigChange) return
    onRecurringConfigChange({ ...recurringConfig, enabled: checked })
    if (!checked) {
      setOccurrences(buildSchedule())
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.dateTime()}</CardTitle>
        <CardDescription>{m.whenWillYourEventTakePlace()}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-2 xs:grid-cols-2">
          <div>
            <Label htmlFor={fromDateId} className="text-xs text-muted-foreground mb-1 block">
              {m.dateFrom()}
            </Label>
            <DatePicker id={fromDateId} value={multiFrom} onChange={handleFromChange} />
          </div>
          <div>
            <Label htmlFor={toDateId} className="text-xs text-muted-foreground mb-1 block">
              {m.dateTo()}
            </Label>
            <DatePicker id={toDateId} value={multiTo} onChange={handleToChange} />
          </div>
        </div>

        <form.Subscribe
          selector={(state: { values: { occurrences?: OccurrenceForm[] } }) =>
            (state.values.occurrences?.length ?? 0) > 0
          }
        >
          {(hasOccurrences: boolean) => hasOccurrences && <EventOccurrencesEditor form={form} />}
        </form.Subscribe>

        {onRecurringConfigChange && recurringConfig && (
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between">
              <Label
                htmlFor={canRecur ? recurringToggleId : undefined}
                className="text-sm font-medium"
              >
                {m.recurringEvents()}
              </Label>
              {canRecur ? (
                <Switch
                  id={recurringToggleId}
                  checked={recurringConfig.enabled}
                  onCheckedChange={(checked) => handleRecurringToggle(!!checked)}
                />
              ) : (
                <Link
                  to="/dashboard/subscription"
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Lock className="h-3.5 w-3.5" />
                  <span>{m.upgradeNow()}</span>
                </Link>
              )}
            </div>

            {recurringConfig.enabled && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {m.recurringRepeatEvery()}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        value={recurringConfig.interval}
                        className="bg-background w-20 max-w-[60px]"
                        onChange={(e) =>
                          onRecurringConfigChange({
                            ...recurringConfig,
                            interval: Math.max(1, Number.parseInt(e.target.value, 10) || 1),
                          })
                        }
                      />
                      <Select
                        value={recurringConfig.frequencyType}
                        onValueChange={(value: 'days' | 'weeks' | 'months') =>
                          onRecurringConfigChange({ ...recurringConfig, frequencyType: value })
                        }
                      >
                        <SelectTrigger className="bg-background">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="days">
                            {recurringConfig.interval === 1 ? m.recurringDay() : m.recurringDays()}
                          </SelectItem>
                          <SelectItem value="weeks">
                            {recurringConfig.interval === 1
                              ? m.recurringWeek()
                              : m.recurringWeeks()}
                          </SelectItem>
                          <SelectItem value="months">
                            {recurringConfig.interval === 1
                              ? m.recurringMonth()
                              : m.recurringMonths()}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {m.recurringNumberOfEvents()}
                    </Label>
                    <Input
                      type="number"
                      min={2}
                      max={52}
                      value={recurringConfig.count}
                      className="bg-background"
                      onChange={(e) =>
                        onRecurringConfigChange({
                          ...recurringConfig,
                          count: Math.min(
                            52,
                            Math.max(2, Number.parseInt(e.target.value, 10) || 2),
                          ),
                        })
                      }
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Button type="button" variant="outline" size="sm" onClick={handleApplyRecurrence}>
                    {m.applyRecurrence()}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    {m.recurringSummary({
                      count: recurringConfig.count,
                      interval: formatInterval(
                        recurringConfig.interval,
                        recurringConfig.frequencyType,
                      ),
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
